import React, { useEffect, useState } from 'react'
import { Chrome } from '@uiw/react-color'
import { Controller } from 'react-hook-form'
import { useBenefits } from '../BenefitsProvider'
import {
  TextField,
  InputField,
  SelectField,
  MultiSelectField,
  PhoneField,
} from '~/components/shared/FormFields'
import { Button } from '~/lib/ui/button'
import { useTranslation } from '~/hooks/useTranslation'
import ResumeUpload from './ResumeUpload'
import FileAttachmentDropzone from './FileAttachmentDropzone'
import { Form, FormLabel } from '~/lib/ui/form'
import { cn } from '~/lib/utils'
import { useInterestKeys } from '~/components/ActionPlan/InterestKeys'
import { useTimelineKeys } from '~/components/ActionPlan/SetTimelineForm'

const OrientationCallForm = () => {
  const { t } = useTranslation()
  const { control, message, submitted, select } = useBenefits()
  const disabled = !message || submitted
  return (
    <ServiceRequestForm>
      <TextField
        control={control}
        name="message"
        label={t('schedule_modal.coach_introduction.message_label')}
      />
      <div className="flex justify-center">
        {/* Tooltip wrapper */}
        <div
          className="relative ml-auto"
          data-toggle="tooltip"
          data-placement="top"
          title={!message ? t('schedule_modal.complete_form_tooltip') : ''}
        >
          <Button
            variant={select ? 'default' : 'coachcta'}
            disabled={disabled} // Disable the button if the message is empty
          >
            {t('schedule_modal.next_button')}
          </Button>
        </div>
      </div>
    </ServiceRequestForm>
  )
}

const NetworkingStrategyForm = () => {
  const { t } = useTranslation('NetworkingStrategy.networking_strategy_form')
  const { message, control, submitted } = useBenefits()
  const disabled = !message || submitted
  return (
    <ServiceRequestForm>
      <TextField control={control} name="message" label={t('message_label')} />
      <div
        className="relative ml-auto"
        data-toggle="tooltip"
        data-placement="top"
        title={disabled ? t('complete_form_tooltip') : ''}
      >
        <Button disabled={disabled} variant={'coachcta'}>
          {t('next_button')}
        </Button>
      </div>
    </ServiceRequestForm>
  )
}

const CallForm = ({
  formDisabled,
  electronic,
  schedule = true,
  scheduleCallCta,
  submitCta,
  children,
}) => {
  const { t } = useTranslation('call_form')
  const {
    message,
    control,
    submitted,
    select,
    createServiceRequest,
    serviceRequestType,
  } = useBenefits()

  const disabled =
    (!message && serviceRequestType !== 'ServiceRequest::ExecutiveBio') ||
    submitted ||
    formDisabled
  const messageLabel =
    serviceRequestType === 'ServiceRequest::NegotiationStrategy' ||
    serviceRequestType === 'ServiceRequest::OnboardingSupport'
      ? t('alt_notes_description')
      : t('notes_description')
  return (
    <ServiceRequestForm>
      <TextField control={control} name="message" label={messageLabel} />
      {children}
      <div
        className={cn(
          'flex',
          schedule && electronic
            ? 'flex-col items-center justify-center gap-4'
            : 'justify-center',
        )}
      >
        {/* Tooltip wrapper */}
        {electronic && (
          <div
            className="ml-auto"
            data-toggle="tooltip"
            data-placement="top"
            title={disabled ? t('complete_form_tooltip') : ''}
          >
            <Button
              type="button"
              onClick={createServiceRequest}
              variant={select ? 'default' : 'coachcta'}
              disabled={disabled}
            >
              {submitCta}
            </Button>
          </div>
        )}
        {schedule && electronic && <div className="font-medium">{t('or')}</div>}
        {schedule && (
          <div
            className={cn('relative', electronic ? '' : 'ml-auto')}
            data-toggle="tooltip"
            data-placement="top"
            title={disabled ? t('complete_form_tooltip') : ''}
          >
            <Button
              variant={select ? 'default' : 'coachcta'}
              disabled={disabled}
            >
              {scheduleCallCta || t('next_button')}
            </Button>
          </div>
        )}
      </div>
    </ServiceRequestForm>
  )
}

const LinkedInReviewForm = () => {
  const { t } = useTranslation()
  const {
    message,
    control,
    linkedinUrl,
    submitted,
    select,
    meetingViaCalendly,
  } = useBenefits()
  const disabled = !(message && linkedinUrl) || submitted

  return (
    <ServiceRequestForm>
      <InputField
        control={control}
        name="linkedin_url"
        label={t('schedule_modal.linkedin_optimization.linkedin_url_label')}
      />
      <TextField
        control={control}
        name="message"
        label={t('schedule_modal.linkedin_optimization.message_label')}
      />
      <div className="flex flex-col items-center justify-center gap-4">
        {/* Tooltip wrapper */}
        <div
          className="relative"
          data-toggle="tooltip"
          data-placement="top"
          title={
            !(message && linkedinUrl)
              ? t('schedule_modal.complete_form_tooltip')
              : ''
          }
        ></div>
        <div
          className="relative ml-auto"
          data-toggle="tooltip"
          data-placement="top"
          title={
            !(message && linkedinUrl)
              ? t('schedule_modal.complete_form_tooltip')
              : ''
          }
        >
          <Button
            variant={select ? 'default' : 'coachcta'}
            disabled={disabled} // Disable the button if the message is empty
          >
            {meetingViaCalendly
              ? t('schedule_modal.schedule_call')
              : t('schedule_modal.submit_request')}
          </Button>
        </div>
      </div>
    </ServiceRequestForm>
  )
}

const ResumeReviewForm = () => {
  const { t } = useTranslation('schedule_modal.resume_review')
  const [fileType, setFileType] = useState('upload')
  const {
    control,
    message,
    createServiceRequest,
    meetingViaCalendly,
    seniorityLevel,
    current_user,
    coach,
    setUploadedResumeId,
    resumeReview,
    createRevisionRequest,
    notes,
    resumeId,
    uploadedResumeId,
    remote_content,
    serviceRequestForm,
    submitted,
    select,
  } = useBenefits()

  const revisionRequest = resumeReview !== null

  const variant = select ? 'default' : 'coachcta'

  return (
    <ServiceRequestForm>
      {revisionRequest ? (
        <p>{t('intro_text_revision')}</p>
      ) : (
        <p>{t('intro_text')}</p>
      )}
      {!revisionRequest && (
        <div className="flex items-center justify-between">
          <Button
            type="button"
            variant={fileType == 'upload' ? variant : 'outline'}
            onClick={() => {
              setFileType('upload')
            }}
          >
            {t('upload_resume')}
          </Button>
          <div className="subtitle">{t('or')}</div>
          <Button
            type="button"
            variant={fileType == 'select' ? 'coachcta' : 'outline'}
            onClick={() => {
              setFileType('select')
            }}
            disabled={current_user.candidate_resumes.length == 0} // Disable the button if there are no resumes
          >
            {t('choose_resume')}
          </Button>
        </div>
      )}
      {fileType == 'upload' ? (
        <ResumeUpload
          containerClassName="px-0 pb-0"
          resumeReview={true}
          setUploadedResumeId={setUploadedResumeId}
          revisionRequest={revisionRequest}
          serviceRequestForm={serviceRequestForm}
        />
      ) : (
        <SelectField
          control={control}
          name="resume_document_id"
          label={t('select_resume_label')}
          choices={current_user.candidate_resumes}
        />
      )}
      {!revisionRequest && (
        <SelectField
          control={control}
          name="seniority_level"
          label={t('seniority_level_label')}
          choices={[
            {
              value: 'Early Career Professional',
              label: t('early_career'),
            },
            {
              value: 'Experienced Professional',
              label: t('experienced'),
            },
            {
              value: 'Manager',
              label: t('manager'),
            },
            { value: 'Director', label: t('director') },
            { value: 'VP', label: t('vp') },
            { value: 'C-Suite / Executive', label: t('executive') },
          ]}
        />
      )}
      <TextField
        control={control}
        name={revisionRequest ? 'notes' : 'message'}
        label={t('message_label')}
      />
      {!revisionRequest && (
        <>
          <hr />
          <div className="subtitle">{t('example_jobs')}</div>
          <p>{t('example_jobs_description')}</p>
          <InputField
            control={control}
            name="example_job_1"
            label={t('example_job_1_label')}
          />
          <InputField
            control={control}
            name="example_job_2"
            label={t('example_job_2_label')}
          />
          <InputField
            control={control}
            name="example_job_3"
            label={t('example_job_3_label')}
          />
        </>
      )}
      <div className="flex justify-center">
        {/* Tooltip wrapper */}
        <div
          className="relative"
          data-toggle="tooltip"
          data-placement="top"
          title={!message ? t('complete_form_tooltip') : ''}
        >
          {revisionRequest ? (
            <div className="flex flex-col items-center justify-center gap-4">
              {/* Tooltip wrapper */}
              <div
                className="relative"
                data-toggle="tooltip"
                data-placement="top"
              >
                <Button
                  type="button"
                  onClick={createServiceRequest}
                  variant={select ? 'default' : 'coachcta'}
                  disabled={
                    revisionRequest
                      ? !(notes && remote_content) || submitted
                      : !(message && seniorityLevel) || submitted
                  } // Disable th
                >
                  {t('request_final_review')}
                </Button>
              </div>
              <div className="font-medium">
                {t('schedule_modal.linkedin_optimization.or')}
              </div>
              <div
                className="relative"
                data-toggle="tooltip"
                data-placement="top"
              >
                <Button
                  variant={select ? 'default' : 'coachcta'}
                  disabled={!(notes && remote_content) || submitted}
                >
                  {t('schedule_modal.schedule_call')}
                </Button>
              </div>
            </div>
          ) : !meetingViaCalendly || !coach || coach.isLiaison ? (
            <Button
              type="button"
              onClick={
                revisionRequest ? createRevisionRequest : createServiceRequest
              }
              variant={select ? 'default' : 'coachcta'}
              disabled={!(message && seniorityLevel) || submitted} // Disable the button if the message is empty
            >
              {t('request_review')}
            </Button>
          ) : (
            <Button
              variant={select ? 'default' : 'coachcta'}
              disabled={
                (!message &&
                  !(uploadedResumeId || resumeId) &&
                  !seniorityLevel) ||
                submitted
              } // Disable the button if the message is empty
            >
              {t('next_button')}
            </Button>
          )}
        </div>
      </div>
    </ServiceRequestForm>
  )
}

const CareerPivotPlanningForm = () => {
  const { t } = useTranslation('CareerPivotPlanning.career_pivot_planning_form')
  const { message, control, submitted } = useBenefits()
  const disabled = !message || submitted
  return (
    <ServiceRequestForm>
      <TextField
        control={control}
        name="message"
        label={t('notes_description')}
      />
      <div className="flex flex-col gap-4 md:flex-row">
        <InputField
          control={control}
          name="most_recent_role"
          label={t('most_recent_role')}
          className="w-full"
        />
        <InputField
          control={control}
          name="desired_role"
          label={t('desired_role')}
          className="w-full"
        />
      </div>
      <div>{t('not_sure_whats_next')}</div>
      <div
        className="relative ml-auto"
        data-toggle="tooltip"
        data-placement="top"
        title={disabled ? t('complete_form_tooltip') : ''}
      >
        <Button disabled={disabled} variant={'coachcta'}>
          {t('next_button')}
        </Button>
      </div>
    </ServiceRequestForm>
  )
}

const ExecutiveBioForm = () => {
  const { t } = useTranslation('executive_bio_form')
  const {
    control,
    setUploadedResumeId,
    serviceRequestForm,
    linkedinUrl,
    uploadedResumeId,
    mostRecentResume,
  } = useBenefits()

  const disabled = !linkedinUrl || !uploadedResumeId
  return (
    <CallForm formDisabled={disabled}>
      <InputField
        control={control}
        name="linkedin_url"
        label={t('linkedin_url_label')}
      />

      <ResumeUpload
        containerClassName="px-0 pb-0"
        resumeReview={true}
        setUploadedResumeId={setUploadedResumeId}
        serviceRequestForm={serviceRequestForm}
        mostRecentResume={mostRecentResume}
      />
    </CallForm>
  )
}

const TargetCompanyResearchForm = () => {
  const { t } = useTranslation('target_company_research_form')
  const {
    control,
    company_sizes,
    sector,
    companySize,
    location,
    industry,
    message,
    submitted,
    setMeetingViaCalendly,
    select,
  } = useBenefits()
  const companySizeChoices = Object.keys(company_sizes)
  const disabled =
    !industry || !sector || !companySize || !location || !message || submitted

  useEffect(() => {
    if (
      typeof window.initTypeahead === 'function' &&
      typeof Bloodhound === 'function'
    ) {
      window.initTypeahead()
    }
  }, [])

  useEffect(() => {
    setMeetingViaCalendly(false)
  }, [setMeetingViaCalendly])

  return (
    <ServiceRequestForm>
      <div>
        <FormLabel>{t('industry_label')}</FormLabel>
        <InputField control={control} name="industry" />
        {select && <InputField control={control} name="industry_2" />}
      </div>
      <SelectField
        control={control}
        name="sector"
        label={t('sector_label')}
        placeholder={t('select_one')}
        choices={[
          { value: 'Any', label: t('any') },
          { value: 'Public', label: t('public') },
          { value: 'Private', label: t('private') },
        ]}
      />
      <div>
        <FormLabel>{t('example_title_label')}</FormLabel>
        <InputField control={control} name="example_job_1" />
        <InputField control={control} name="example_job_2" />
      </div>
      <SelectField
        control={control}
        name="company_size"
        label={t('company_size_label')}
        placeholder={t('select_one')}
        choices={companySizeChoices.map((size) => ({
          value: size,
          label: size,
        }))}
      />
      <InputField
        control={control}
        name="location"
        label={t('location_label')}
        data-source="locations"
        inputClassName={'autocomplete locations-typeahead'}
      />
      <TextField control={control} name="message" label={t('message_label')} />
      <div
        className="relative ml-auto"
        data-toggle="tooltip"
        data-placement="top"
        title={disabled ? t('complete_form_tooltip') : ''}
      >
        <Button
          variant={select ? 'default' : 'coachcta'}
          disabled={disabled} // Disable the button if the message is empty
        >
          {t('submit_request')}
        </Button>
      </div>
    </ServiceRequestForm>
  )
}

const TargetIndustryResearchForm = () => {
  const { t } = useTranslation('target_industry_research_form')
  const {
    control,
    company_sizes,
    sector,
    location,
    industry,
    message,
    submitted,
    setMeetingViaCalendly,
    select,
    exampleCompany1,
    exampleCompany2,
  } = useBenefits()
  const companySizeChoices = Object.keys(company_sizes)
  const disabled =
    !industry ||
    !sector ||
    !location ||
    !message ||
    submitted ||
    (!exampleCompany1 && !exampleCompany2)

  useEffect(() => {
    if (
      typeof window.initTypeahead === 'function' &&
      typeof Bloodhound === 'function'
    ) {
      window.initTypeahead()
    }
  }, [])

  useEffect(() => {
    setMeetingViaCalendly(false)
  }, [setMeetingViaCalendly])

  return (
    <ServiceRequestForm>
      <div>
        <FormLabel>{t('industry_label')}</FormLabel>
        <InputField control={control} name="industry" />
        {select && <InputField control={control} name="industry_2" />}
      </div>
      <SelectField
        control={control}
        name="sector"
        label={t('sector_label')}
        placeholder={t('select_one')}
        choices={[
          { value: 'Any', label: t('any') },
          { value: 'Public', label: t('public') },
          { value: 'Private', label: t('private') },
        ]}
      />
      <div>
        <FormLabel>{t('example_title_label')}</FormLabel>
        <InputField control={control} name="example_company_1" />
        <InputField control={control} name="example_company_2" />
      </div>
      <SelectField
        control={control}
        name="company_size"
        label={t('company_size_label')}
        placeholder={t('select_one')}
        choices={companySizeChoices.map((size) => ({
          value: size,
          label: size,
        }))}
      />
      <InputField
        control={control}
        name="location"
        label={t('location_label')}
        data-source="locations"
        inputClassName={'autocomplete locations-typeahead'}
      />
      <TextField control={control} name="message" label={t('message_label')} />
      <div
        className="relative ml-auto"
        data-toggle="tooltip"
        data-placement="top"
        title={disabled ? t('complete_form_tooltip') : ''}
      >
        <Button
          variant={select ? 'default' : 'coachcta'}
          disabled={disabled} // Disable the button if the message is empty
        >
          {t('submit_request')}
        </Button>
      </div>
    </ServiceRequestForm>
  )
}

const RecruiterGuidanceSupportForm = () => {
  const { t } = useTranslation('recruiter_guidance_support_form')
  const { control, industries, industry_role, location, industry, salary } =
    useBenefits()
  const salaries = t('salary_ranges', {
    returnObjects: true,
  })

  const disabled = !industry_role || !location || !industry || !salary

  useEffect(() => {
    if (
      typeof window.initTypeahead === 'function' &&
      typeof Bloodhound === 'function'
    ) {
      window.initTypeahead()
    }
  }, [])

  return (
    <CallForm formDisabled={disabled}>
      <InputField
        control={control}
        name="industry_role"
        label={t('role_label')}
        data-source="roles"
        inputClassName={'autocomplete roles-typeahead'}
      />
      <InputField
        control={control}
        name="location"
        label={t('location_label')}
        data-source="locations"
        inputClassName={'autocomplete locations-typeahead'}
      />
      <SelectField
        control={control}
        name="industry"
        label={t('industry_label')}
        placeholder={t('select_one')}
        choices={industries.map((size) => ({
          value: size,
          label: size,
        }))}
      />
      <SelectField
        control={control}
        name="salary"
        label={t('salary_range_label')}
        placeholder={t('select_one')}
        choices={salaries.map((size) => ({
          value: size,
          label: size,
        }))}
      />
    </CallForm>
  )
}

const JobSearchStrategyForm = () => {
  const { t } = useTranslation('job_search_strategy_form')
  const interestKeys = useInterestKeys()
  const { control, onboardingGoal, message, select } = useBenefits()
  const disabled = !message
  return (
    <ServiceRequestForm formDisabled={disabled}>
      <SelectField
        control={control}
        name="desired_goal"
        label={t('desired_goal_label')}
        choices={Object.entries(interestKeys).map(([key, value]) => ({
          value: key,
          label: value,
        }))}
      />
      <SelectField
        control={control}
        name="desired_timeline"
        label={t('desired_timeline_label')}
        placeholder={t('select_one')}
        choices={Object.entries(useTimelineKeys(t, onboardingGoal)).map(
          ([key, value]) => ({
            value: key,
            label: value,
          }),
        )}
      />
      <TextField control={control} name="message" label={t('message_label')} />
      <div className="flex justify-center">
        {/* Tooltip wrapper */}
        <div
          className="relative ml-auto"
          data-toggle="tooltip"
          data-placement="top"
          title={disabled ? t('complete_form_tooltip') : ''}
        >
          <Button
            variant={select ? 'default' : 'coachcta'}
            disabled={disabled} // Disable the button if the message is empty
          >
            {t('next_button')}
          </Button>
        </div>
      </div>
    </ServiceRequestForm>
  )
}

const InterviewPrepForm = () => {
  const { t } = useTranslation('interview_prep_form')
  const { control, targetRoles, careerLevel, interviewTypes, message, select } =
    useBenefits()
  const disabled = !(message && targetRoles && careerLevel && interviewTypes)
  return (
    <ServiceRequestForm formDisabled={disabled}>
      <InputField
        control={control}
        name="target_roles"
        label={t('target_roles_label')}
        data-source="roles"
        inputClassName={'autocomplete roles-typeahead'}
      />
      <SelectField
        control={control}
        name="career_level"
        label={t('career_level_label')}
        placeholder={t('select_one')}
        choices={t('career_levels').map((level) => ({
          value: level,
          label: level,
        }))}
      />
      <MultiSelectField
        control={control}
        name="interview_types"
        label={t('interview_types_label')}
        placeholder={t('select_one')}
        choices={t('interview_types').map((level) => ({
          value: level,
          label: level,
        }))}
      />
      <TextField control={control} name="message" label={t('message_label')} />
      <div className="flex justify-center">
        {/* Tooltip wrapper */}
        <div
          className="relative ml-auto"
          data-toggle="tooltip"
          data-placement="top"
          title={disabled ? t('complete_form_tooltip') : ''}
        >
          <Button
            variant={select ? 'default' : 'coachcta'}
            disabled={disabled} // Disable the button if the message is empty
          >
            {t('next_button')}
          </Button>
        </div>
      </div>
    </ServiceRequestForm>
  )
}

const MockInterviewForm = () => {
  const { t } = useTranslation('mock_interview_form')
  const {
    control,
    message,
    select,
    setUploadedResumeId,
    uploadedResumeId,
    serviceRequestForm,
    targetJobDescription,
  } = useBenefits()
  const disabled = !(message && targetJobDescription && uploadedResumeId)
  return (
    <ServiceRequestForm formDisabled={disabled}>
      <TextField
        control={control}
        name="target_job_description"
        label={t('target_job_description_label')}
        data-source="roles"
        innerClassName={'min-h-[200px]'}
      />
      <ResumeUpload
        containerClassName="px-0 pb-0"
        resumeReview={true}
        setUploadedResumeId={setUploadedResumeId}
        serviceRequestForm={serviceRequestForm}
        translationKey="mock_interview_form.resume_upload"
        className={'min-h-[10rem]'}
      />
      <InputField
        control={control}
        name="interview_with"
        label={t('interview_with_label')}
      />
      <InputField
        control={control}
        name="interview_type"
        label={t('interview_type_label')}
      />
      <TextField control={control} name="message" label={t('message_label')} />
      <div className="flex justify-center">
        {/* Tooltip wrapper */}
        <div
          className="relative ml-auto"
          data-toggle="tooltip"
          data-placement="top"
          title={disabled ? t('complete_form_tooltip') : ''}
        >
          <Button
            variant={select ? 'default' : 'coachcta'}
            disabled={disabled} // Disable the button if the message is empty
          >
            {t('next_button')}
          </Button>
        </div>
      </div>
    </ServiceRequestForm>
  )
}

const ResumeTailoringForm = ({ submitCta }) => {
  const { t } = useTranslation('resume_tailoring_form')
  const {
    control,
    message,
    select,
    setUploadedResumeId,
    uploadedResumeId,
    serviceRequestForm,
    meetingViaCalendly,
    targetJobDescription,
    submitted,
  } = useBenefits()
  const disabled =
    !message || !uploadedResumeId || !targetJobDescription || submitted
  const cta = meetingViaCalendly ? t('next_button') : submitCta
  return (
    <ServiceRequestForm formDisabled={disabled}>
      <ResumeUpload
        containerClassName="px-0 pb-0"
        resumeReview={true}
        setUploadedResumeId={setUploadedResumeId}
        serviceRequestForm={serviceRequestForm}
        translationKey="resume_tailoring_form.resume_upload"
      />
      <TextField
        control={control}
        name="target_job_description"
        label={t('target_job_description_label')}
        data-source="roles"
      />
      <TextField control={control} name="message" label={t('message_label')} />
      <div className="flex justify-center">
        {/* Tooltip wrapper */}
        <div
          className="relative ml-auto"
          data-toggle="tooltip"
          data-placement="top"
          title={disabled ? t('complete_form_tooltip') : ''}
        >
          <Button
            variant={select ? 'default' : 'coachcta'}
            disabled={disabled} // Disable the button if the message is empty
          >
            {cta}
          </Button>
        </div>
      </div>
    </ServiceRequestForm>
  )
}

const CoverLetterReviewForm = () => {
  const translationScope = 'cover_letter_review_form'
  const { t } = useTranslation(translationScope)
  const {
    control,
    select,
    setUploadedResumeId,
    serviceRequestForm,
    submitted,
    meetingViaCalendly,
  } = useBenefits()
  const getValues = serviceRequestForm.getValues
  const requiredResumeMissing =
    !meetingViaCalendly && !getValues('resume_document_id')
  const disabled =
    submitted ||
    !getValues('candidate_document') ||
    getValues('candidate_document').length === 0 ||
    requiredResumeMissing
  return (
    <ServiceRequestForm formDisabled={disabled}>
      <FileAttachmentDropzone
        translationScope={translationScope}
        fieldName="candidate_document"
      />
      {!meetingViaCalendly && (
        <ResumeUpload
          containerClassName="px-0 pb-0"
          resumeReview={true}
          setUploadedResumeId={setUploadedResumeId}
          serviceRequestForm={serviceRequestForm}
          translationKey="cover_letter_review_form.resume_upload"
        />
      )}
      <TextField
        control={control}
        name="target_job_description"
        label={t('target_job_description_label')}
        data-source="roles"
      />
      <TextField control={control} name="message" label={t('message_label')} />
      <div className="flex justify-center">
        <div
          className="relative ml-auto"
          data-toggle="tooltip"
          data-placement="top"
          title={disabled ? t('complete_form_tooltip') : ''}
        >
          <Button
            variant={select ? 'default' : 'coachcta'}
            disabled={disabled} // Disable the button if the message is empty
          >
            {t('next_button')}
          </Button>
        </div>
      </div>
    </ServiceRequestForm>
  )
}

const ResumeWritingForm = ({ submitCta }) => {
  const { t } = useTranslation('resume_writing_form')
  const {
    control,
    message,
    select,
    setUploadedResumeId,
    serviceRequestForm,
    meetingViaCalendly,
    targetJobDescription,
    workExperience,
    educationHistory,
  } = useBenefits()
  const disabled =
    !message || !workExperience || !educationHistory || !targetJobDescription
  const cta = meetingViaCalendly ? t('next_button') : submitCta
  return (
    <ServiceRequestForm formDisabled={disabled}>
      <TextField
        control={control}
        name="work_experience"
        label={t('work_experience_label')}
        rows={4}
      />
      <TextField
        control={control}
        name="education_history"
        label={t('education_history_label')}
        rows={3}
      />
      <TextField
        control={control}
        name="target_job_description"
        label={t('target_job_description_label')}
        data-source="roles"
      />
      <ResumeUpload
        containerClassName="px-0 pb-0"
        setUploadedResumeId={setUploadedResumeId}
        serviceRequestForm={serviceRequestForm}
        translationKey="resume_writing_form.resume_upload"
        resumeReview={true}
      />
      <InputField
        control={control}
        name="linkedin_url"
        label={t('linkedin_url_label')}
        className="max-w-sm"
      />
      <TextField
        control={control}
        name="achievements"
        label={t('achievements_label')}
        rows={2}
      />
      <TextField
        control={control}
        name="skills_certifications"
        label={t('skills_certifications_label')}
        rows={2}
      />
      {/* Message always last */}
      <TextField control={control} name="message" label={t('message_label')} />
      <div className="flex justify-center">
        <div
          className="relative ml-auto"
          data-toggle="tooltip"
          data-placement="top"
          title={disabled ? t('complete_form_tooltip') : ''}
        >
          <Button variant={select ? 'default' : 'coachcta'} disabled={disabled}>
            {cta}
          </Button>
        </div>
      </div>
    </ServiceRequestForm>
  )
}

const ServiceRequestForm = ({ children }) => {
  const { serviceRequestForm, onSubmit } = useBenefits()
  return (
    <Form {...serviceRequestForm}>
      <form
        onSubmit={serviceRequestForm.handleSubmit(onSubmit)}
        className="flex flex-col gap-6"
      >
        {children}
      </form>
    </Form>
  )
}

const GeneralCheckInForm = () => {
  const { t } = useTranslation('GeneralCheckIn.general_check_in_form')
  const generalTopics = [
    {
      value: 'Personal Branding & Executive Presence',
      label: t('personal_branding_label'),
    },
    {
      value: 'Networking Strategy & Relationship Management',
      label: t('networking_strategy_label'),
    },
    {
      value: 'Job Search Strategy & Market Positioning',
      label: t('job_search_strategy_label'),
    },
    {
      value: 'Interview Preparation & Storytelling',
      label: t('interview_preparation_label'),
    },
    {
      value: 'Emotional Resilience & Confidence Building',
      label: t('emotional_resilience_label'),
    },
  ]
  const { control, message, submitted, generalTopic, setMeetingDuration } =
    useBenefits()
  const disabled = !message || !generalTopic || submitted
  const handleMeetingDurationChange = (duration) => {
    setMeetingDuration(duration)
  }
  return (
    <ServiceRequestForm>
      <SelectField
        control={control}
        name="general_topic"
        placeholder={t('select_one')}
        label={t(
          'topic_label',
          'Select a general topic for your meeting. (required)',
        )}
        choices={generalTopics}
      />
      <TextField
        control={control}
        name="message"
        label={t(
          'message_label',
          'What would you like to discuss with your coach? (required)',
        )}
      />
      <div className="flex justify-center">
        <div
          className="relative ml-auto"
          data-toggle="tooltip"
          data-placement="top"
          title={
            disabled
              ? t(
                  'complete_form_tooltip',
                  'Please complete all fields to continue.',
                )
              : ''
          }
        >
          <div className="flex space-x-3">
            <Button
              disabled={disabled}
              onClick={() => handleMeetingDurationChange(30)}
              variant="select"
            >
              {t('schedule_30_minute_call', 'Schedule 30 Minute Call')}
            </Button>
            <Button
              disabled={disabled}
              onClick={() => handleMeetingDurationChange(50)}
              variant="select"
            >
              {t('schedule_50_minute_call', 'Schedule 50 Minute Call')}
            </Button>
          </div>
        </div>
      </div>
    </ServiceRequestForm>
  )
}

const SalaryResearchForm = () => {
  const { t } = useTranslation('salary_research_form')
  const {
    control,
    company_sizes,
    sector,
    companySize,
    location,
    industry,
    message,
    submitted,
    setMeetingViaCalendly,
    select,
  } = useBenefits()
  const companySizeChoices = Object.keys(company_sizes)
  const disabled =
    !industry || !sector || !companySize || !location || !message || submitted

  useEffect(() => {
    if (
      typeof window.initTypeahead === 'function' &&
      typeof Bloodhound === 'function'
    ) {
      window.initTypeahead()
    }
  }, [])

  useEffect(() => {
    setMeetingViaCalendly(false)
  }, [setMeetingViaCalendly])

  return (
    <ServiceRequestForm>
      <div>
        <FormLabel>{t('industry_label')}</FormLabel>
        <InputField control={control} name="industry" />
        {select && <InputField control={control} name="industry_2" />}
      </div>
      <SelectField
        control={control}
        name="sector"
        label={t('sector_label')}
        placeholder={t('select_one')}
        choices={[
          { value: 'Any', label: t('any') },
          { value: 'Public', label: t('public') },
          { value: 'Private', label: t('private') },
        ]}
      />
      <div>
        <FormLabel>{t('example_title_label')}</FormLabel>
        <InputField control={control} name="example_job_1" />
        <InputField control={control} name="example_job_2" />
      </div>
      <SelectField
        control={control}
        name="company_size"
        label={t('company_size_label')}
        placeholder={t('select_one')}
        choices={companySizeChoices.map((size) => ({
          value: size,
          label: size,
        }))}
      />
      <InputField
        control={control}
        name="location"
        label={t('location_label')}
        data-source="locations"
        inputClassName={'autocomplete locations-typeahead'}
      />
      <TextField control={control} name="message" label={t('message_label')} />
      <div
        className="relative ml-auto"
        data-toggle="tooltip"
        data-placement="top"
        title={disabled ? t('complete_form_tooltip') : ''}
      >
        <Button
          variant={select ? 'default' : 'coachcta'}
          disabled={disabled} // Disable the button if the message is empty
        >
          {t('submit_request')}
        </Button>
      </div>
    </ServiceRequestForm>
  )
}

const BusinessCardForm = () => {
  const { t } = useTranslation('business_card_creation_form')
  const {
    control,
    phone_number,
    email,
    industry,
    style,
    submitted,
    setMeetingViaCalendly,
    select,
    professionalName,
  } = useBenefits()
  const disabled =
    !phone_number ||
    !email ||
    !industry ||
    !style ||
    !professionalName ||
    submitted

  // Default colors as HSVA objects. Note Chrome component prefers to use HSVA. Convert to hex for storage later.
  const DEFAULT_PRIMARY_HSVA = { h: 231, s: 78, v: 49, a: 1 } // #1a237e
  const DEFAULT_SECONDARY_HSVA = { h: 0, s: 0, v: 38, a: 1 } // #616161

  useEffect(() => {
    setMeetingViaCalendly(false)
  }, [setMeetingViaCalendly])

  return (
    <ServiceRequestForm>
      <div className="flex w-full flex-col gap-y-4 md:grid md:grid-cols-2 md:gap-x-8">
        <InputField
          control={control}
          name="professional_name"
          label={t('professional_name_label')}
          className="w-full"
        />
        <InputField
          control={control}
          name="job_title"
          label={t('job_title_label')}
          className="w-full"
        />
        <InputField
          control={control}
          name="website_url"
          label={t('website_url_label')}
          className="w-full"
        />
        <InputField
          control={control}
          name="linkedin_url"
          label={t('linkedin_url_label')}
          className="w-full"
        />
        <PhoneField
          control={control}
          name="phone_number"
          label={t('phone_number_label')}
          className="w-full"
        />
        <InputField
          control={control}
          name="email"
          label={t('email_label')}
          className="w-full"
        />
        <InputField
          control={control}
          name="industry"
          label={t('industry_label')}
          className="w-full"
        />
        <InputField
          control={control}
          name="qr_code_link"
          label={t('qr_code_label')}
        />
        <SelectField
          control={control}
          name="style"
          label={t('style_label')}
          placeholder={t('select_one')}
          choices={[
            { value: 'modern', label: t('styles.modern') },
            { value: 'classic', label: t('styles.classic') },
          ]}
          className="w-full"
        />
        <div className="col-span-2">
          <div className="mb-2 font-bold">{t('colors_label')}</div>
          <div className="flex flex-col gap-4 md:flex-row md:gap-20">
            <div className="flex flex-col gap-2">
              <label htmlFor="primary-color-picker">{t('primary_label')}</label>
              <Controller
                control={control}
                name="colors.primary"
                render={({ field }) => {
                  return (
                    <Chrome
                      color={field.value || DEFAULT_PRIMARY_HSVA}
                      onChange={(color) => field.onChange(color.hsva)}
                      className="!w-full md:!w-60"
                    />
                  )
                }}
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="font-medium" htmlFor="secondary-color-picker">
                {t('secondary_label')}
              </label>
              <Controller
                control={control}
                name="colors.secondary"
                render={({ field }) => (
                  <Chrome
                    color={field.value || DEFAULT_SECONDARY_HSVA}
                    onChange={(color) => field.onChange(color.hsva)}
                    className="!w-full md:!w-60"
                  />
                )}
              />
            </div>
          </div>
        </div>
      </div>
      <div
        className="relative mx-auto mt-6 md:ml-auto md:mr-0 "
        data-toggle="tooltip"
        data-placement="top"
        title={disabled ? t('complete_form_tooltip') : ''}
      >
        <Button variant={select ? 'default' : 'coachcta'} disabled={disabled}>
          {t('submit_request')}
        </Button>
      </div>
    </ServiceRequestForm>
  )
}

const StrengthsAssessmentForm = () => {
  const translationScope = 'strengths_assessment_form'
  const { t } = useTranslation(translationScope)
  const {
    control,
    submitted,
    select,
    serviceRequestForm,
    serviceRequest,
    createServiceRequest,
    message,
  } = useBenefits()
  const {
    clifton_strengths_code: cliftonStrengthsCode,
    clifton_strengths_link: cliftonStrengthsLink,
  } = serviceRequest || {}
  const getValues = serviceRequestForm.getValues
  const [requestSubmitted, setRequestSubmitted] = useState(false)
  const handleSubmitRequest = () => {
    setRequestSubmitted(true)
    createServiceRequest()
  }
  const requiredDocumentMissing =
    cliftonStrengthsCode &&
    (!getValues('document_id') || getValues('document_id').length === 0)
  const disabled =
    submitted || requestSubmitted || requiredDocumentMissing || !message
  return (
    <ServiceRequestForm formDisabled={disabled}>
      {!serviceRequest && !cliftonStrengthsCode ? (
        <>
          <TextField
            control={control}
            name="message"
            label={t('strengths_notes_description')}
          />
          <div className={cn('flex justify-center')}>
            {/* Tooltip wrapper */}
            <div
              className="ml-auto"
              data-toggle="tooltip"
              data-placement="top"
              title={disabled ? t('complete_form_tooltip') : ''}
            >
              <Button
                type="button"
                onClick={handleSubmitRequest}
                variant={select ? 'default' : 'coachcta'}
                disabled={disabled}
              >
                {t('request_code')}
              </Button>
            </div>
          </div>
        </>
      ) : !cliftonStrengthsCode ? (
        t('research_team_reviewing_request')
      ) : (
        <div className="flex flex-col gap-4">
          <p>{t('form_instructions')}</p>
          <p>
            <span className="font-bold">{t('your_link')}:</span>{' '}
            {cliftonStrengthsLink && isValidUrl(cliftonStrengthsLink) ? (
              <a
                href={cliftonStrengthsLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 text-link underline"
              >
                {cliftonStrengthsLink}
              </a>
            ) : (
              cliftonStrengthsLink || null
            )}
          </p>
          <p>
            <span className="font-bold">{t('your_code')}:</span>{' '}
            {cliftonStrengthsCode}
          </p>
          <FileAttachmentDropzone
            translationScope={translationScope}
            fieldName="document_id"
          />
          <div className="flex justify-center">
            <div
              className="relative ml-auto"
              data-toggle="tooltip"
              data-placement="top"
              title={disabled ? t('complete_form_tooltip') : ''}
            >
              <Button
                variant={select ? 'default' : 'coachcta'}
                disabled={disabled}
              >
                {t('next_button')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </ServiceRequestForm>
  )
}

const isValidUrl = (url) => {
  try {
    new URL(url)
    return true
  } catch {
    return false
  }
}

export {
  OrientationCallForm,
  LinkedInReviewForm,
  ResumeReviewForm,
  CallForm,
  ExecutiveBioForm,
  RecruiterGuidanceSupportForm,
  TargetCompanyResearchForm,
  CareerPivotPlanningForm,
  ServiceRequestForm,
  JobSearchStrategyForm,
  InterviewPrepForm,
  NetworkingStrategyForm,
  MockInterviewForm,
  CoverLetterReviewForm,
  ResumeTailoringForm,
  GeneralCheckInForm,
  TargetIndustryResearchForm,
  SalaryResearchForm,
  BusinessCardForm,
  ResumeWritingForm,
  StrengthsAssessmentForm,
}
